import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../services/api';
import { Chart, registerables } from 'chart.js';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import SensorMap from '../components/SensorMap';
import { Droplets } from 'lucide-react';

import '../styles/GlobalPageStyles.css';
import ModuleAccessDenied from '../components/ui/ModuleAccessDenied';
import type { NumericValue } from '@/utils/numeric';
import { toNum, toNumOr } from '@/utils/numeric';

Chart.register(...registerables);

// Helper to add auth header to fetch requests
const getAuthHeaders = () => {
    const token = getToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
};

interface SensorReading {
    timestamp: number;
    temperature?: NumericValue;
    humidity?: NumericValue;
    conductivity?: NumericValue;
    pH?: NumericValue;
    nitrogen?: NumericValue;
    phosphorus?: NumericValue;
    potassium?: NumericValue;
}

interface DeviceInfo {
    device_id?: string;
    firmware_version?: string;
    chip_model?: string;
    chip_revision?: string;
    flash_size?: number;
    free_heap?: number;
    wifi_rssi?: number;
}

type TimeRange = '24h' | '7d' | '30d' | 'custom';

// For Capacitor/mobile apps, use environment variable or fallback
const getApiBase = (): string => {
    // Check for the correct environment variable name
    if (import.meta.env.VITE_API_BASE_URL) {
        // Remove /api suffix if it exists, since we'll add it in endpoints
        let url = import.meta.env.VITE_API_BASE_URL;
        if (url.endsWith('/api')) {
            url = url.slice(0, -4);
        }
        return url;
    }
    if (import.meta.env.VITE_API_BASE) {
        return import.meta.env.VITE_API_BASE;
    }
    // On web, use origin. On Capacitor, this will be overridden by env var
    return window.location.origin;
};

const API_BASE = getApiBase();
const API_ENDPOINTS = {
    readings: `${API_BASE}/api/sensors/readings`,
    latest: `${API_BASE}/api/sensors/latest`,
    device: `${API_BASE}/api/sensors/device`,
    devices: `${API_BASE}/api/sensors/devices`
};

const POLL_INTERVAL = 60000; // 1min

const SoilSensorDashboard: React.FC = () => {
    const { user } = useAuth();
    const [connected, setConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [currentReading, setCurrentReading] = useState<SensorReading>({} as SensorReading);
    const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({});
    const [timeRange, setTimeRange] = useState<TimeRange>('24h');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [modalChart, setModalChart] = useState<{ chartId: string; visible: boolean }>({ chartId: '', visible: false });
    const [allReadings, setAllReadings] = useState<SensorReading[]>([]);
    const [devices, setDevices] = useState<string[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(() => {
        return localStorage.getItem('soil-selected-device-id');
    });
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [registerDeviceId, setRegisterDeviceId] = useState('');
    const [registerError, setRegisterError] = useState<string | null>(null);
    const [registerSuccess, setRegisterSuccess] = useState(false);
    const [showMapModal, setShowMapModal] = useState(false);
    const [devicesFetchError, setDevicesFetchError] = useState<string | null>(null);
    const [devicesFetching, setDevicesFetching] = useState(true);
    const [moduleBlocked, setModuleBlocked] = useState(false);
    
    // ✅ Single source of truth for ideal ranges
    const IdealRanges = {
        temperature: { min: 15, max: 35, unit: '°C' },
        humidity: { min: 40, max: 70, unit: '%' },
        conductivity: { min: 400, max: 800, unit: 'µS/cm' },
        pH: { min: 6.0, max: 7.5, unit: '' },
        nitrogen: { min: 50, max: 100, unit: 'mg/kg' },
        phosphorus: { min: 50, max: 100, unit: 'mg/kg' },
        potassium: { min: 150, max: 300, unit: 'mg/kg' }
    };

    // Chart configurations
    const chartConfigs: { [key: string]: { label: string; dataKey: keyof SensorReading; color: string; title: string } } = {
        nitrogenLineChart: { label: 'Nitrogen (mg/kg)', dataKey: 'nitrogen', color: '#2ecc71', title: 'Nitrogen (N)' },
        phosphorusLineChart: { label: 'Phosphorus (mg/kg)', dataKey: 'phosphorus', color: '#f1c40f', title: 'Phosphorus (P)' },
        potassiumLineChart: { label: 'Potassium (mg/kg)', dataKey: 'potassium', color: '#9b59b6', title: 'Potassium (K)' },
        tempLineChart: { label: 'Temperature (°C)', dataKey: 'temperature', color: '#e67e22', title: 'Soil Temperature' },
        humidityLineChart: { label: 'Humidity (%)', dataKey: 'humidity', color: '#3498db', title: 'Soil Moisture' },
        pHLineChart: { label: 'pH', dataKey: 'pH', color: '#8e44ad', title: 'pH Level' },
        condLineChart: { label: 'Conductivity (µS/cm)', dataKey: 'conductivity', color: '#16a085', title: 'Conductivity' }
    };

    // ✅ Helper to safely parse numeric values
    const parseFloat = (value: any): number => {
        return typeof value === 'string' ? Number(value) : value || 0;
    };

    // ✅ Single getStatus function
    const getStatus = (value: number | undefined, min: number, max: number): { text: string; className: string } => {
        if (value === undefined) return { text: 'Unknown', className: 'value-unknown' };
        if (value >= min && value <= max) return { text: 'Good', className: 'value-good' };
        if (value >= min * 0.8 && value <= max * 1.2) return { text: 'Warning', className: 'value-warning' };
        return { text: 'Poor', className: 'value-bad' };
    };

    const chartsRef = useRef<{ [key: string]: Chart }>({});
    const modalChartRef = useRef<Chart | null>(null);
    const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Fetch functions
    const fetchReadings = async (range: TimeRange, startDate?: string, endDate?: string): Promise<SensorReading[]> => {
        try {
            let url = `${API_ENDPOINTS.readings}`;

            // If custom dates provided, use them instead of range
            if (range === 'custom' && startDate && endDate) {
                url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
            } else {
                url += `?range=${range}`;
            }
            if (selectedDeviceId) {
                url += `&deviceId=${encodeURIComponent(selectedDeviceId)}`;
            }

            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setAllReadings(data);
            return data;
        } catch (error) {
            console.error('Error fetching readings:', error);
            setConnected(false);
            return [];
        }
    };

    const fetchLatestReading = async (): Promise<void> => {
        try {
            let url = API_ENDPOINTS.latest;
            if (selectedDeviceId) {
                url += `?deviceId=${encodeURIComponent(selectedDeviceId)}`;
            }
            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            if (response.status === 404) {
                // Device selected but no latest reading; consider server connected
                setConnected(true);
                setCurrentReading({} as SensorReading);
                setLastUpdate(null);
                return;
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data: SensorReading = await response.json();

            setConnected(true);
            setCurrentReading(data);
            setLastUpdate(new Date(data.timestamp));
        } catch (error) {
            console.error('Error fetching latest reading:', error);
            setConnected(false);
        }
    };

    const fetchDeviceInfo = async (): Promise<void> => {
        try {
            let url = API_ENDPOINTS.device;
            if (selectedDeviceId) {
                url = `${API_ENDPOINTS.device}/${encodeURIComponent(selectedDeviceId)}`;
            }
            const response = await fetch(url, {
                headers: getAuthHeaders()
            });
            if (response.status === 404) {
                // No device info available for this device - clear and continue
                setDeviceInfo({});
                return;
            }
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const result = await response.json();
            // Handle different response formats
            const data: DeviceInfo = result.success ? result.data : result;
            setDeviceInfo(data);
        } catch (error) {
            console.error('Error fetching device info:', error);
            setDeviceInfo({});
        }
    };

    const fetchDevices = async (): Promise<void> => {
        try {
            setDevicesFetching(true);
            setDevicesFetchError(null);
            
            const response = await fetch(API_ENDPOINTS.devices, {
                headers: getAuthHeaders()
            });
            
            if (response.status === 403) {
                const data = await response.json().catch(() => null);
                if (data?.error?.code === 'MODULE_NOT_SUBSCRIBED') {
                    setModuleBlocked(true);
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: Failed to fetch devices`);
            }
            
            const data: { devices: string[] } = await response.json();
            setDevices(data.devices || []);
            
            // Reconcile selected device with fetched list
            if (data.devices && data.devices.length > 0) {
                const saved = selectedDeviceId;
                if (saved && data.devices.includes(saved)) {
                    // keep saved
                } else {
                    const newSel = data.devices[0];
                    setSelectedDeviceId(newSel);
                    localStorage.setItem('soil-selected-device-id', newSel);
                }
            } else {
                // No devices available
                setSelectedDeviceId(null);
                localStorage.removeItem('soil-selected-device-id');
            }
        } catch (error) {
            console.error('Error fetching devices:', error);
            const errorMsg = error instanceof Error ? error.message : 'Failed to load devices. Please check your connection and API configuration.';
            setDevicesFetchError(errorMsg);
            setDevices([]);
        } finally {
            setDevicesFetching(false);
        }
    };

    const handleRegisterDevice = async (): Promise<void> => {
        setRegisterError(null);
        setRegisterSuccess(false);

        if (!registerDeviceId.trim()) {
            setRegisterError('Please enter a device ID');
            return;
        }

        try {
            const response = await fetch(`${API_BASE}/api/sensors/register-device`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ deviceId: registerDeviceId.trim() })
            });

            const data = await response.json();

            if (!response.ok) {
                setRegisterError(data.error || 'Failed to register device');
                return;
            }

            setRegisterSuccess(true);
            setRegisterDeviceId('');

            // Refresh device list
            await fetchDevices();

            // Close modal after a short delay
            setTimeout(() => {
                setShowRegisterModal(false);
                setRegisterSuccess(false);
            }, 1500);

        } catch (error) {
            console.error('Error registering device:', error);
            setRegisterError('Failed to register device. Please try again.');
        }
    };

    // Chart rendering
    const downsampleReadings = (readings: SensorReading[], bucketMinutes = 10, percentThreshold = 10): SensorReading[] => {
        if (!readings.length) return [];
        const bucketMs = bucketMinutes * 60 * 1000;
        const result: SensorReading[] = [];
        let lastValues: SensorReading = {} as SensorReading;
        let lastBucket: number | null = null;

        readings.forEach(r => {
            const bucket = Math.floor(r.timestamp / bucketMs);
            if (lastBucket !== bucket) {
                result.push(r);
                lastValues = { ...r };
                lastBucket = bucket;
            } else {
                let significantChange = false;
                for (const key of ['temperature', 'humidity', 'conductivity', 'pH', 'nitrogen', 'phosphorus', 'potassium'] as const) {
                    if (r[key] !== undefined && lastValues[key] !== undefined) {
                        const v1 = toNumOr(lastValues[key]);
                        const v2 = toNumOr(r[key]);
                        if (v1 === 0) continue;
                        if (Math.abs((v2 - v1) / v1) * 100 > percentThreshold) {
                            significantChange = true;
                            break;
                        }
                    }
                }
                if (significantChange) {
                    result.push(r);
                    lastValues = { ...r };
                }
            }
        });
        return result;
    };

    const renderCharts = async (readings: SensorReading[]): Promise<void> => {
        const dsReadings = downsampleReadings(readings, 10, 10);
        dsReadings.sort((a, b) => a.timestamp - b.timestamp);
        
        const labels = dsReadings.map(r => new Date(r.timestamp).toLocaleString());

        Object.entries(chartConfigs).forEach(([chartId, config]) => {
            const canvas = document.getElementById(chartId) as HTMLCanvasElement;
            if (!canvas) return;

            const data = dsReadings.map(r => toNum(r[config.dataKey]));

            if (chartsRef.current[chartId]) {
                chartsRef.current[chartId].data.labels = labels;
                chartsRef.current[chartId].data.datasets[0].data = data;
                chartsRef.current[chartId].update();
            } else {
                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                chartsRef.current[chartId] = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels,
                        datasets: [{
                            label: config.label,
                            data: data,
                            borderColor: config.color,
                            backgroundColor: config.color + '20',
                            fill: false,
                            tension: 0.1
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: true,
                        scales: {
                            x: { display: false }
                        },
                        plugins: {
                            legend: { display: false }
                        }
                    }
                });
            }
        });
    };

    // Modal chart rendering
    const openChartModal = (chartId: string) => {
        setModalChart({ chartId, visible: true });
        
        // Small delay to ensure modal DOM is ready
        setTimeout(() => renderModalChart(chartId), 100);
    };

    const closeChartModal = () => {
        if (modalChartRef.current) {
            modalChartRef.current.destroy();
            modalChartRef.current = null;
        }
        setModalChart({ chartId: '', visible: false });
    };

    const renderModalChart = (chartId: string) => {
        const canvas = document.getElementById('modalChartCanvas') as HTMLCanvasElement;
        if (!canvas) return;

        const config = chartConfigs[chartId];
        if (!config) return;

        // Sort readings by timestamp
        const sortedReadings = [...allReadings].sort((a, b) => a.timestamp - b.timestamp);
        
        const labels = sortedReadings.map(r => new Date(r.timestamp).toLocaleString());
        const data = sortedReadings.map(r => toNum(r[config.dataKey]));

        if (modalChartRef.current) {
            modalChartRef.current.destroy();
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        modalChartRef.current = new Chart(ctx, {
            type: 'line',
            data: {
                labels,
                datasets: [{
                    label: config.label,
                    data: data,
                    borderColor: config.color,
                    backgroundColor: config.color + '20',
                    fill: true,
                    tension: 0.1,
                    pointRadius: 3,
                    pointHoverRadius: 5
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { 
                        display: true,
                        ticks: {
                            maxRotation: 45,
                            minRotation: 45
                        }
                    },
                    y: {
                        display: true,
                        beginAtZero: false
                    }
                },
                plugins: {
                    legend: { 
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                }
            }
        });
    };

    // Polling — restart and reload devices when org changes
    useEffect(() => {
        setDevices([]);
        setSelectedDeviceId(null);
        // Load devices first; selectedDeviceId effect below triggers initial fetches
        fetchDevices();

        pollingTimerRef.current = setInterval(() => {
            fetchLatestReading();
            fetchDeviceInfo();
        }, POLL_INTERVAL);

        return () => {
            if (pollingTimerRef.current) clearInterval(pollingTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.organisation_id]);

    // Fetch latest + device info when device selection changes
    useEffect(() => {
        fetchLatestReading();
        fetchDeviceInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDeviceId]);

    // Load chart data
    useEffect(() => {
        if (timeRange === 'custom' && customStartDate && customEndDate) {
            fetchReadings(timeRange, customStartDate, customEndDate).then(renderCharts);
        } else {
            fetchReadings(timeRange).then(renderCharts);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeRange, customStartDate, customEndDate, selectedDeviceId]);

    // Update charts when new reading arrives
    useEffect(() => {
        if (currentReading.timestamp) {
            if (timeRange === 'custom' && customStartDate && customEndDate) {
                fetchReadings(timeRange, customStartDate, customEndDate).then(renderCharts);
            } else {
                fetchReadings(timeRange).then(renderCharts);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentReading, selectedDeviceId]);

    if (moduleBlocked) {
        return (
            <div className="page-container">
                <PageHeader
                    title="Soil Sensor Dashboard"
                    subtitle="Monitor real-time soil conditions and historical trends"
                    icon={<Droplets className="h-6 w-6 text-white" />}
                    showBackButton={true}
                    showDashboardButton={true}
                />
                <main className="page-main">
                    <div className="page-main-inner">
                        <ModuleAccessDenied moduleName="Soil Sensors" />
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="page-container">
            <PageHeader
                title="Soil Sensor Dashboard"
                subtitle="Monitor real-time soil conditions and historical trends"
                icon={<Droplets className="h-6 w-6 text-white" />}
                showBackButton={true}
                showDashboardButton={true}
            />

            <main className="page-main">
                <div className="page-main-inner">
                    {/* Device Tabs with Add Device and Map Buttons */}
                    <div className="mb-6 border-b border-neutral-200 overflow-x-auto">
                        {devicesFetchError && (
                            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-700 font-medium">⚠️ Unable to load devices</p>
                                <p className="text-xs text-red-600 mt-1">{devicesFetchError}</p>
                                <p className="text-xs text-red-600 mt-2">API Base: {API_BASE}</p>
                                <button
                                    onClick={() => fetchDevices()}
                                    className="mt-2 px-3 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                        
                        {devicesFetching && devices.length === 0 && !devicesFetchError && (
                            <div className="p-4 text-neutral-600 text-sm">
                                Loading devices...
                            </div>
                        )}
                        
                        <div className="flex items-center justify-between gap-4 min-w-max">
                            <div className="flex gap-2 flex-1">
                                {devices.length > 0 && devices.map((id) => (
                                    <button
                                        key={id}
                                        onClick={() => {
                                            setSelectedDeviceId(id);
                                            localStorage.setItem('soil-selected-device-id', id);
                                        }}
                                        className={`
                                            px-6 py-3 font-medium text-sm border-b-2 transition-colors whitespace-nowrap
                                            ${selectedDeviceId === id
                                                ? 'border-primary-600 text-primary-600'
                                                : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
                                            }
                                        `}
                                    >
                                        Device {id}
                                        <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-neutral-100 text-neutral-700">
                                            Soil Sensor
                                        </span>
                                    </button>
                                ))}
                                {devices.length === 0 && !devicesFetchError && !devicesFetching && (
                                    <div className="text-neutral-500 text-sm py-3">
                                        No devices registered yet
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setShowMapModal(true)}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm whitespace-nowrap"
                                >
                                    View Map
                                </button>
                                <button
                                    onClick={() => setShowRegisterModal(true)}
                                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm whitespace-nowrap"
                                >
                                    + Add Device
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Placeholder when no device is selected */}
                    {!selectedDeviceId && (
                        <div className="flex items-center justify-center py-16 px-4">
                            <div className="text-center">
                                <div className="text-6xl mb-4">🌱</div>
                                <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Device Selected</h3>
                                <p className="text-neutral-500">Please select a device from the tabs above to view its sensor data</p>
                            </div>
                        </div>
                    )}

                    {selectedDeviceId && (
                        <>
                    <div className="status">
                        <div className="status-item">
                            <div className={`status-dot ${connected ? 'online' : 'offline'}`}></div>
                            <span>{connected ? 'Connected to Server' : 'Disconnected from Server'}</span>
                        </div>
                        <div className="status-item">
                            <span>{lastUpdate ? `Last updated: ${lastUpdate.toLocaleString()}` : 'No data received yet'}</span>
                        </div>
                    </div>
                    
                    <table className="sensor-table">
                        <thead>
                            <tr>
                                <th>Parameter</th>
                                <th>Value</th>
                                <th>Unit</th>
                                <th>Status</th>
                                <th>Ideal Range</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>🌡️ Temperature</td>
                                <td className={getStatus(parseFloat(currentReading.temperature), IdealRanges.temperature.min, IdealRanges.temperature.max).className}>
                                    {parseFloat(currentReading.temperature).toFixed(1)}
                                </td>
                                <td>{IdealRanges.temperature.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.temperature), IdealRanges.temperature.min, IdealRanges.temperature.max).className}>
                                    {getStatus(parseFloat(currentReading.temperature), IdealRanges.temperature.min, IdealRanges.temperature.max).text}
                                </td>
                                <td>{IdealRanges.temperature.min}-{IdealRanges.temperature.max}{IdealRanges.temperature.unit}</td>
                            </tr>
                            <tr>
                                <td>💧 Humidity</td>
                                <td className={getStatus(parseFloat(currentReading.humidity), IdealRanges.humidity.min, IdealRanges.humidity.max).className}>
                                    {parseFloat(currentReading.humidity).toFixed(1)}
                                </td>
                                <td>{IdealRanges.humidity.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.humidity), IdealRanges.humidity.min, IdealRanges.humidity.max).className}>
                                    {getStatus(parseFloat(currentReading.humidity), IdealRanges.humidity.min, IdealRanges.humidity.max).text}
                                </td>
                                <td>{IdealRanges.humidity.min}-{IdealRanges.humidity.max}{IdealRanges.humidity.unit}</td>
                            </tr>
                            <tr>
                                <td>⚡ Conductivity</td>
                                <td className={getStatus(parseFloat(currentReading.conductivity), IdealRanges.conductivity.min, IdealRanges.conductivity.max).className}>
                                    {parseFloat(currentReading.conductivity).toFixed(0)}
                                </td>
                                <td>{IdealRanges.conductivity.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.conductivity), IdealRanges.conductivity.min, IdealRanges.conductivity.max).className}>
                                    {getStatus(parseFloat(currentReading.conductivity), IdealRanges.conductivity.min, IdealRanges.conductivity.max).text}
                                </td>
                                <td>{IdealRanges.conductivity.min}-{IdealRanges.conductivity.max} {IdealRanges.conductivity.unit}</td>
                            </tr>
                            <tr>
                                <td>🧪 pH</td>
                                <td className={getStatus(parseFloat(currentReading.pH), IdealRanges.pH.min, IdealRanges.pH.max).className}>
                                    {parseFloat(currentReading.pH).toFixed(2)}
                                </td>
                                <td></td>
                                <td className={getStatus(parseFloat(currentReading.pH), IdealRanges.pH.min, IdealRanges.pH.max).className}>
                                    {getStatus(parseFloat(currentReading.pH), IdealRanges.pH.min, IdealRanges.pH.max).text}
                                </td>
                                <td>{IdealRanges.pH.min}-{IdealRanges.pH.max}</td>
                            </tr>
                            <tr>
                                <td>🟦 Nitrogen (N)</td>
                                <td className={getStatus(parseFloat(currentReading.nitrogen), IdealRanges.nitrogen.min, IdealRanges.nitrogen.max).className}>
                                    {parseFloat(currentReading.nitrogen).toFixed(0)}
                                </td>
                                <td>{IdealRanges.nitrogen.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.nitrogen), IdealRanges.nitrogen.min, IdealRanges.nitrogen.max).className}>
                                    {getStatus(parseFloat(currentReading.nitrogen), IdealRanges.nitrogen.min, IdealRanges.nitrogen.max).text}
                                </td>
                                <td>{IdealRanges.nitrogen.min}-{IdealRanges.nitrogen.max} {IdealRanges.nitrogen.unit}</td>
                            </tr>
                            <tr>
                                <td>🟨 Phosphorus (P)</td>
                                <td className={getStatus(parseFloat(currentReading.phosphorus), IdealRanges.phosphorus.min, IdealRanges.phosphorus.max).className}>
                                    {parseFloat(currentReading.phosphorus).toFixed(0)}
                                </td>
                                <td>{IdealRanges.phosphorus.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.phosphorus), IdealRanges.phosphorus.min, IdealRanges.phosphorus.max).className}>
                                    {getStatus(parseFloat(currentReading.phosphorus), IdealRanges.phosphorus.min, IdealRanges.phosphorus.max).text}
                                </td>
                                <td>{IdealRanges.phosphorus.min}-{IdealRanges.phosphorus.max} {IdealRanges.phosphorus.unit}</td>
                            </tr>
                            <tr>
                                <td>🟪 Potassium (K)</td>
                                <td className={getStatus(parseFloat(currentReading.potassium), IdealRanges.potassium.min, IdealRanges.potassium.max).className}>
                                    {parseFloat(currentReading.potassium).toFixed(0)}
                                </td>
                                <td>{IdealRanges.potassium.unit}</td>
                                <td className={getStatus(parseFloat(currentReading.potassium), IdealRanges.potassium.min, IdealRanges.potassium.max).className}>
                                    {getStatus(parseFloat(currentReading.potassium), IdealRanges.potassium.min, IdealRanges.potassium.max).text}
                                </td>
                                <td>{IdealRanges.potassium.min}-{IdealRanges.potassium.max} {IdealRanges.potassium.unit}</td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <div className="controls" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <label htmlFor="timeRangeSelect" style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Show data from:</label>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <button
                                    onClick={() => setTimeRange('24h')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        backgroundColor: timeRange === '24h' ? '#3b82f6' : '#e5e7eb',
                                        color: timeRange === '24h' ? 'white' : '#374151',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    24 Hours
                                </button>
                                <button
                                    onClick={() => setTimeRange('7d')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        backgroundColor: timeRange === '7d' ? '#3b82f6' : '#e5e7eb',
                                        color: timeRange === '7d' ? 'white' : '#374151',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    7 Days
                                </button>
                                <button
                                    onClick={() => setTimeRange('30d')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        backgroundColor: timeRange === '30d' ? '#3b82f6' : '#e5e7eb',
                                        color: timeRange === '30d' ? 'white' : '#374151',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    30 Days
                                </button>
                                <button
                                    onClick={() => setTimeRange('custom')}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        border: 'none',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem',
                                        fontWeight: '500',
                                        cursor: 'pointer',
                                        backgroundColor: timeRange === 'custom' ? '#3b82f6' : '#e5e7eb',
                                        color: timeRange === 'custom' ? 'white' : '#374151',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Custom Range
                                </button>
                            </div>
                        </div>
                        
                        {timeRange === 'custom' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', paddingLeft: '1rem' }}>
                                <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>From:</label>
                                <input
                                    type="datetime-local"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    style={{
                                        padding: '0.5rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem'
                                    }}
                                />
                                <label style={{ fontSize: '0.875rem', fontWeight: '500' }}>To:</label>
                                <input
                                    type="datetime-local"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    style={{
                                        padding: '0.5rem',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '0.875rem'
                                    }}
                                />
                                {customStartDate && customEndDate && (
                                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                        Selected range: {new Date(customStartDate).toLocaleString()} - {new Date(customEndDate).toLocaleString()}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    
                    <div className="charts-container">
                        {Object.entries(chartConfigs).map(([chartId, config]) => (
                            <div key={chartId} className="chart-card" onClick={() => openChartModal(chartId)} style={{ cursor: 'pointer' }}>
                                <div className="chart-title">{config.title} 🔍</div>
                                <canvas id={chartId} height="120"></canvas>
                            </div>
                        ))}
                    </div>
                    
                    <div className="device-info">
                        <h3>📟 Device Information</h3>
                        <div className="device-summary">
                            <span><strong>Device ID:</strong> {deviceInfo.device_id ?? '--'}; </span>
                            <span><strong>Firmware:</strong> {deviceInfo.firmware_version ?? '--'}; </span>
                            <span><strong>Chip Model:</strong> {deviceInfo.chip_model ?? '--'}; </span>
                            <span><strong>Chip Rev:</strong> {deviceInfo.chip_revision ?? '--'}; </span>
                            <span><strong>Flash:</strong> {deviceInfo.flash_size ? `${(deviceInfo.flash_size / (1024 * 1024)).toFixed(1)} MB` : '--'}; </span>
                            <span><strong>Free Heap:</strong> {deviceInfo.free_heap ? `${(deviceInfo.free_heap / 1024).toFixed(1)} KB` : '--'}; </span>
                            <span><strong>WiFi RSSI:</strong> {deviceInfo.wifi_rssi ? `${deviceInfo.wifi_rssi} dBm` : '--'}; </span>
                        </div>
                    </div>
                    </>
                    )}
                </div>
            </main>

            {/* Modal for fullscreen chart */}
            {modalChart.visible && (
                <div className="chart-modal" onClick={closeChartModal}>
                    <div className="chart-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="chart-modal-header">
                            <h2>{chartConfigs[modalChart.chartId]?.title}</h2>
                            <button className="modal-close-btn" onClick={closeChartModal}>✕</button>
                        </div>
                        <div className="chart-modal-body">
                            <canvas id="modalChartCanvas"></canvas>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for device registration */}
            {showRegisterModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setShowRegisterModal(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-semibold text-neutral-900">Register New Device</h2>
                            <button
                                onClick={() => setShowRegisterModal(false)}
                                className="text-neutral-400 hover:text-neutral-600 text-2xl leading-none"
                            >
                                ✕
                            </button>
                        </div>

                        <p className="text-sm text-neutral-600 mb-4">
                            Enter the Device ID printed on your soil sensor to register it to your organization.
                        </p>

                        <div className="mb-4">
                            <label htmlFor="deviceId" className="block text-sm font-medium text-neutral-700 mb-2">
                                Device ID
                            </label>
                            <input
                                id="deviceId"
                                type="text"
                                value={registerDeviceId}
                                onChange={(e) => setRegisterDeviceId(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleRegisterDevice()}
                                placeholder="e.g., 11, 12, 13..."
                                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                            />
                        </div>

                        {registerError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-sm text-red-700">{registerError}</p>
                            </div>
                        )}

                        {registerSuccess && (
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-sm text-green-700">Device registered successfully!</p>
                            </div>
                        )}

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setShowRegisterModal(false)}
                                className="px-4 py-2 text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRegisterDevice}
                                disabled={registerSuccess}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Register Device
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for sensor map */}
            {showMapModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowMapModal(false)}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[80vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
                            <div>
                                <h2 className="text-2xl font-semibold text-neutral-900">Sensor Locations</h2>
                                <p className="text-sm text-neutral-600 mt-1">View all your organization's sensors on the map</p>
                            </div>
                            <button
                                onClick={() => setShowMapModal(false)}
                                className="text-neutral-400 hover:text-neutral-600 text-2xl leading-none"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="flex-1 p-6 overflow-hidden">
                            <SensorMap
                                onDeviceClick={(deviceId) => {
                                    setSelectedDeviceId(deviceId);
                                    localStorage.setItem('soil-selected-device-id', deviceId);
                                    setShowMapModal(false);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SoilSensorDashboard;